import { queryOptions } from '@tanstack/react-query'
import type { AdmView } from './admDashboard'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * ADM -> Discovery runs si Discovery schedule, calea noua. Perechile vechi, din
 * `application/controllers/Adm.php`:
 *
 *   runs / runsData          -> GET  /adm/runs, /adm/runs/table
 *   run / runTargetsData     -> GET  /adm/runs/{id}, /adm/runs/{id}/targets
 *   runTabs / targetTabs     -> GET  /adm/runs/{id}/drawer, /adm/runs/targets/{id}/drawer
 *   cancelRun / runJob       -> POST /adm/runs/{id}/cancel, /adm/runs/job/{job}
 *   schedules / scheduleTabs -> GET  /adm/schedules, /adm/schedules/{id}/drawer
 *   scanNow                  -> POST /adm/schedules/scan-now
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * CE POATE FACE OMUL VINE DE LA SERVER. `canWrite` si `canCancel` spun doar
 * daca sa se deseneze un buton; refuzul insusi e pe server, la fiecare capat.
 * Un gard desenat in browser nu e un gard.
 *
 * FIRMA NU SE TRIMITE. Platforma o intreaba o data, sus, si serverul o citeste
 * din jeton (`AdmFilters`) - deci ecranul nici n-ar avea ce sa trimita.
 */

/** O optiune de filtru, asa cum o trimite serverul. */
export interface Option {
  value: string
  label: string
}

/** Filtrele barei, asa cum le-a citit serverul. */
export interface AdmRunFilters {
  days: number
  company_id: number
  relay_device_id: number
  run_type: number
  state: number
}

export interface RunsShell {
  pageTitle: string
  pageHeader: string
  breadcrumbs: string[]
  windows: number[]
  filters: AdmRunFilters
  relays: Option[]
  runTypes: Option[]
  states: Option[]
  /** Cate ruleaza ACUM - aceeasi cifra ca pe tabloul de bord. */
  active: number
  /** Banda celor cinci vederi, filtrata pe server: pe drept si pe ce s-a mutat. */
  views: AdmView[]
  canWrite: boolean
  /** Ce poate porni de mana un operator - `Adm::$manualJobs`. */
  jobs: Option[]
  /** Fara server de cronuri scris in `.env`, butoanele n-au pe cine suna. */
  cronConfigured: boolean
}

/** Un rand din lista de scanari, cu tot ce a socotit serverul pe el. */
export interface RunRow {
  ID: number
  DT_RowId: string
  startedAt: string
  finishedAt: string
  runType: number
  typeLabel: string
  state: number
  stateLabel: string
  trigger: number
  triggerLabel: string
  targetsTotal: number
  targetsResponded: number
  progress: number
  ciCreated: number
  ciUpdated: number
  relationsCreated: number
  errorsCount: number
  companyId: number
  companyName: string
  relayName: string
  duration: string
  /** Numai o scanare care inca merge se poate opri - hotarat pe server. */
  canCancel: boolean
}

/** Fisa unei scanari: randul, plus cele doua nume pe care lista nu le poarta. */
export interface RunSheet extends RunRow {
  rangeName: string
  startedByName: string
}

export interface RunsTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: RunRow[]
  active: number
}

export interface OutcomeCount {
  outcome: number
  title: string
  value: number
}

/** Un port deschis, ca eticheta: `445`, `161/udp`; `title` e serviciul, la hover. */
export interface PortTag {
  port: string
  title: string
}

/** Un rand din tabelul de adrese. Gasirile brute raman pe server. */
export interface TargetRow {
  ID: number
  DT_RowId: string
  ip: string
  hostname: string
  /** Numele intreg (wks02.cecdemo.local), cand e altul decat numele scurt; gol altfel. */
  fqdn?: string
  mac: string
  manufacturer: string
  ports: string[]
  portsMore: number
  /** Aceleasi porturi ca etichete: numarul, si serviciul pentru hover. */
  portTags: PortTag[]
  /** Cate porturi deschise are adresa, toate, nu doar cele aratate. */
  portsTotal: number
  kinds: string[]
  kindsMore: number
  /** Indiciile agentului: dc, smb, rdp, winrm... */
  hints: string[]
  hintsMore: number
  pingMs: number | null
  ttl: number | null
  /** Sistemul ghicit, ca la agent: "Windows (TTL 128)", "Windows (ports 135/5985)"; gol cand nu se poate. */
  os: string
  /** Felul ghicit cu regula din Pending (AutoClassifier); gol cand nimic nu voteaza. */
  kindGuess: string
  outcome: number
  outcomeLabel: string
  /** Ce s-a vazut, pentru rand: in loc de "Alive, not classified", felul ghicit sau porturile. */
  foundLabel: string
  ciId: number
  ciName: string
  updatedAt: string
  /** Daca sertarul are ce arata pe randul asta. */
  hasDetail: boolean
}

export interface RunPage {
  pageTitle: string
  pageHeader: string
  breadcrumbs: string[]
  run: RunSheet
  breakdown: OutcomeCount[]
  /** Coloanele pe care le deseneaza tabelul de adrese, in ordine, ca CHEI. */
  targetColumns: string[]
  canWrite: boolean
}

export interface TargetsPage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: TargetRow[]
  columns: string[]
}

export interface RunDrawer {
  run: RunSheet
  breakdown: OutcomeCount[]
  rows: TargetRow[]
  total: number
}

export interface TargetSheet {
  ID: number
  runId: number
  ip: string
  hostname: string
  /** Numele intreg (wks02.cecdemo.local), cand e altul decat numele scurt; gol altfel. */
  fqdn?: string
  mac: string
  manufacturer: string
  uuid: string
  ports: string[]
  hints: string[]
  pingMs: number | null
  ttl: number | null
  serviceDetails: string
  virtualHostUnique: string
  virtualHostMac: string
  outcomeLabel: string
  updatedAt: string
}

export interface ServiceRow {
  proto: string
  port: number | null
  kind: string
  /** Lamurirea felului, goala cand agentul a invatat unul nou. */
  kindLabel: string
  summary: string
  seenAt: string
}

export interface TargetDrawer {
  target: TargetSheet
  rows: ServiceRow[]
  total: number
}

export interface ScheduleRow {
  ID: number
  DT_RowId: string
  name: string
  mode: number
  modeLabel: string
  excluded: boolean
  active: boolean
  companyId: number
  companyName: string
  relayDeviceId: number
  relayName: string
  relayLastSeen: string
  ipFrom: string
  ipTo: string
  recurrenceLabel: string
  /** Socotit din recurenta, niciodata tinut minte. */
  nextRunAt: string
  lastRunId: number
  lastRunAt: string
  lastRunState: number
  lastRunStateLabel: string
  targetsResponded: number
  targetsTotal: number
  ciCreated: number
  errorsCount: number
}

export interface SchedulesPage {
  pageTitle: string
  pageHeader: string
  breadcrumbs: string[]
  windows: number[]
  filters: AdmRunFilters
  relays: Option[]
  ranges: ScheduleRow[]
  counts: { total: number; excluded: number; neverRun: number }
  views: AdmView[]
  canWrite: boolean
}

export interface ScheduleDrawer {
  range: ScheduleRow
}

/** Ce trimite ecranul cand cere lista de scanari. */
export interface RunsTableRequest {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderDir: 'asc' | 'desc'
  search: string
  days: number
  relay_device_id: string
  run_type: string
  run_state: string
}

/** Ce trimite fisa cand cere adresele. Sortarea se cere pe CHEIE, nu pe indice. */
export interface TargetsRequest {
  draw: number
  start: number
  length: number
  orderKey: string
  orderDir: 'asc' | 'desc'
  search: string
  outcome: string
}

export function runsShellQuery(days: number, relay: string, type: string, state: string) {
  const params = new URLSearchParams({ days: String(days), relay_device_id: relay, run_type: type, run_state: state })

  return queryOptions({
    queryKey: ['adm', 'runs', 'shell', days, relay, type, state],
    queryFn: () => apiGet<RunsShell>(`/adm/runs?${params.toString()}`),
  })
}

export function runsTableQuery(r: RunsTableRequest) {
  const params = new URLSearchParams({
    draw: String(r.draw),
    start: String(r.start),
    length: String(r.length),
    orderColumn: String(r.orderColumn),
    orderDir: r.orderDir,
    search: r.search,
    days: String(r.days),
    relay_device_id: r.relay_device_id,
    run_type: r.run_type,
    run_state: r.run_state,
  })

  return queryOptions({
    queryKey: ['adm', 'runs', 'table', r],
    queryFn: () => apiGet<RunsTablePage>(`/adm/runs/table?${params.toString()}`),
  })
}

export function runQuery(id: number) {
  return queryOptions({
    queryKey: ['adm', 'runs', 'one', id],
    queryFn: () => apiGet<RunPage>(`/adm/runs/${id}`),
  })
}

export function runTargetsQuery(id: number, r: TargetsRequest) {
  const params = new URLSearchParams({
    draw: String(r.draw),
    start: String(r.start),
    length: String(r.length),
    orderKey: r.orderKey,
    orderDir: r.orderDir,
    search: r.search,
    outcome: r.outcome,
  })

  return queryOptions({
    queryKey: ['adm', 'runs', 'targets', id, r],
    queryFn: () => apiGet<TargetsPage>(`/adm/runs/${id}/targets?${params.toString()}`),
  })
}

export function runDrawerQuery(id: number) {
  return queryOptions({
    queryKey: ['adm', 'runs', 'drawer', id],
    queryFn: () => apiGet<RunDrawer>(`/adm/runs/${id}/drawer`),
  })
}

export function targetDrawerQuery(id: number) {
  return queryOptions({
    queryKey: ['adm', 'runs', 'target-drawer', id],
    queryFn: () => apiGet<TargetDrawer>(`/adm/runs/targets/${id}/drawer`),
  })
}

export function schedulesQuery(relay: string) {
  const params = new URLSearchParams({ relay_device_id: relay })

  return queryOptions({
    queryKey: ['adm', 'schedules', relay],
    queryFn: () => apiGet<SchedulesPage>(`/adm/schedules?${params.toString()}`),
  })
}

export function scheduleDrawerQuery(id: number) {
  return queryOptions({
    queryKey: ['adm', 'schedules', 'drawer', id],
    queryFn: () => apiGet<ScheduleDrawer>(`/adm/schedules/${id}/drawer`),
  })
}

/** Opreste o scanare care inca merge. */
export function cancelRun(id: number) {
  return apiPost<{ status: string; message: string }>(`/adm/runs/${id}/cancel`, {})
}

/** Porneste de mana una din treburile programate. */
export function runJob(job: string) {
  return apiPost<{ status: string; message: string; output?: string }>(`/adm/runs/job/${job}`, {})
}

/** Cere unui releu sa scaneze acum. */
export function scanNow(relay: number) {
  const body: Record<string, PostValue> = { relay }

  /* `stalled`: releul nu-si mai ia cererile - cea mai veche asteapta de peste
     zece minute. Cererea s-a pus (sau era deja), dar nu porneste "intr-un minut". */
  return apiPost<{ status: string; queued: boolean; stalled?: boolean; message: string }>('/adm/schedules/scan-now', body)
}

/* ------------------------------------------------------------------ *
 * Cloud collection - cerinta 87
 * ------------------------------------------------------------------ */

/**
 * O firma cu conturi AWS / Azure, cu orele colectarii ei. Fara rand scris pe
 * server, `custom` e false si orele sunt cele implicite (06:15, 18:15).
 */
export interface CloudScheduleRow {
  companyId: number
  companyName: string
  aws: number
  azure: number
  times: string[]
  active: boolean
  custom: boolean
  runRequested: boolean
  nextRunAt: string
  lastRunId: number
  lastRunAt: string
  lastRunState: number
  lastRunStateLabel: string
  lastRunErrors: number
}

export interface CloudSchedulesPage {
  rows: CloudScheduleRow[]
  defaultTimes: string[]
  canWrite: boolean
}

export function cloudSchedulesQuery() {
  return queryOptions({
    queryKey: ['adm', 'schedules', 'cloud'],
    queryFn: () => apiGet<CloudSchedulesPage>('/adm/schedules/cloud'),
  })
}

/** Orele unei firme: "06:15, 18:15"; `active` false = nu colecteaza singura. */
export function saveCloudSchedule(companyId: number, times: string, active: boolean) {
  return apiPost<{ status: string; message: string; times: string[]; active: boolean }>(`/adm/schedules/cloud/${companyId}`, {
    times,
    active: active ? 1 : 0,
  })
}

/** "Run now": cererea ramane si daca serverul de cronuri nu raspunde (`queued`). */
export function runCloudNow(companyId: number) {
  return apiPost<{ status: string; queued: boolean; message: string; output?: string }>(`/adm/schedules/cloud/${companyId}/run-now`, {})
}
